/**
 * Dialogue checks (ADR-136's amendment of 2026-10-28, decisions 1, 2 and 5): the `check` a choice may carry, its
 * validation inside a dialogue file, the base it starts from and its chance of passing. The roll itself is the map
 * server's. Pure.
 */

import { DIALOGUE_CHECK_ATTRIBUTES, type DialogueCheckAttribute } from "../npcs/dialogue-marks.ts";
import { MAX_STANDING_PER, MIN_STANDING_PER, NPC_STANDING_MAX, NPC_STANDING_MIN } from "../npcs/standing.ts";
import type { DialogueAction, DialogueCondition } from "./dialogue.ts";

/**
 * The base of a check before its modifiers.
 */
export const DEFAULT_CHECK_BASE = 30;

/**
 * The lowest chance a check ever shows or rolls against.
 */
export const MIN_CHECK_CHANCE = 5;

/**
 * The highest chance a check ever shows or rolls against.
 */
export const MAX_CHECK_CHANCE = 95;

/**
 * The points of chance one point of the attribute over or under the difficulty is worth.
 */
export const CHECK_POINTS_PER_ATTRIBUTE = 5;

/**
 * The lowest difficulty a check may name.
 */
export const MIN_CHECK_DIFFICULTY = 1;

/**
 * The highest difficulty a check may name. Bible 4.1 sets no attribute range yet; the clamp holds for any value.
 */
export const MAX_CHECK_DIFFICULTY = 50;

/**
 * The largest size, up or down, of one entry of a check's `modifiers`.
 */
export const MAX_CHECK_MODIFIER = 100;

/**
 * What a failed check does next for the character: `final` closes it for good, `retry` opens it again the next
 * in-game day.
 */
export const CHECK_FAIL_RULES = Object.freeze(["final", "retry"] as const);

/**
 * The weights a choice may be marked with.
 */
export const CHOICE_WEIGHTS = Object.freeze(["significant", "defining"] as const);

/**
 * A failure rule.
 */
export type CheckFailRule = (typeof CHECK_FAIL_RULES)[number];

/**
 * A choice's weight.
 */
export type ChoiceWeight = (typeof CHOICE_WEIGHTS)[number];

/**
 * One entry of a check's `modifiers`: the signed points of chance it adds to the base while its conditions hold.
 */
export interface CheckModifier {
  readonly when: readonly DialogueCondition[];
  readonly modifier: number;
}

/**
 * What a failure does: `rule` left out means `retry`; `standing` is the signed change to the character's standing with
 * the conversation's NPC, applied on a failed roll (ADR-146 decision 4.2). `aggressive` is refused by the validator until
 * its row.
 */
export interface CheckFailure {
  readonly rule?: CheckFailRule;
  readonly standing?: number;
}

/**
 * A check's standing bonus (ADR-146 decision 5.2): `per` points of chance for each point of standing with the
 * conversation's NPC, signed, the term capped at -25 to +25.
 */
export interface CheckStandingBonus {
  readonly with: "npc";
  readonly per: number;
}

/**
 * A check a choice carries.
 */
export interface DialogueCheck {
  readonly attribute: DialogueCheckAttribute;
  readonly difficulty: number;
  readonly modifiers?: readonly CheckModifier[];
  readonly standingBonus?: CheckStandingBonus;
  /** Left out means `{ rule: "retry" }`. */
  readonly onFail?: CheckFailure;
  /** Actions run only when the roll passes (ADR-142 decision 4.4), after the choice's own `do`. */
  readonly onPass?: readonly DialogueAction[];
}

/**
 * The failure rule a check applies: its written rule, else `retry`, the default.
 *
 * @param check The check.
 * @returns The rule.
 * @example
 * checkFailRule({ attribute: "might", difficulty: 5 });
 * // "retry"
 */
export function checkFailRule(check: DialogueCheck): CheckFailRule {
  return check.onFail?.rule ?? "retry";
}

const CHECK_FIELDS = ["attribute", "difficulty", "modifiers", "standingBonus", "onFail", "onPass"] as const;
const MODIFIER_FIELDS = ["when", "modifier"] as const;
const RESERVED_FAIL_FIELDS: { readonly [field: string]: string } = Object.freeze({ aggressive: "P2.22-T8" });
const STANDING_BONUS_FIELDS = ["with", "per"] as const;

type Plain = { readonly [key: string]: unknown };

/**
 * Records a problem at a path inside the choice's node.
 */
export type CheckReport = (field: string, message: string) => void;

/**
 * Whether a value is a plain object.
 *
 * @param value Anything.
 * @returns True for an object that is not null or an array.
 */
export function isPlain(value: unknown): value is Plain {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Whether a value is a whole number within a range.
 *
 * @param value Anything.
 * @param min The lowest.
 * @param max The highest.
 * @returns True for an integer from `min` to `max`.
 */
export function wholeIn(value: unknown, min: number, max: number): boolean {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}

/**
 * Checks a choice's `check` value: a closed object with a known attribute, a difficulty in range, an optional list of
 * modifiers and a failure rule; the reserved fields are refused naming their rows.
 *
 * @param value The `check` value.
 * @param report Records a problem.
 * @param path The path of the check.
 * @param conditions Checks a `when` list, as the dialogue validator does for choices.
 * @param actions Checks a `do` list, as the dialogue validator does for choices; it checks `onPass`.
 */
export function checkProblems(
  value: unknown,
  report: CheckReport,
  path: string,
  conditions: (value: unknown, report: CheckReport, path: string) => void,
  actions: (value: unknown, report: CheckReport, path: string) => void,
): void {
  if (!isPlain(value)) {
    report(path, "check must be an object with attribute and difficulty");
    return;
  }
  for (const key of Object.keys(value)) {
    if (!(CHECK_FIELDS as readonly string[]).includes(key)) {
      report(`${path}.${key}`, `${key} is not a field of a check`);
    }
  }
  if (!(DIALOGUE_CHECK_ATTRIBUTES as readonly unknown[]).includes(value["attribute"])) {
    report(`${path}.attribute`, `attribute must be one of ${DIALOGUE_CHECK_ATTRIBUTES.join(", ")}`);
  }
  if (!wholeIn(value["difficulty"], MIN_CHECK_DIFFICULTY, MAX_CHECK_DIFFICULTY)) {
    report(`${path}.difficulty`, `difficulty must be a whole number from ${MIN_CHECK_DIFFICULTY} to ${MAX_CHECK_DIFFICULTY}`);
  }
  if (value["modifiers"] !== undefined) {
    modifiersProblems(value["modifiers"], report, `${path}.modifiers`, conditions);
  }
  if (value["standingBonus"] !== undefined) {
    standingBonusProblems(value["standingBonus"], report, `${path}.standingBonus`);
  }
  if (value["onFail"] !== undefined) {
    failProblems(value["onFail"], report, `${path}.onFail`);
  }
  if (value["onPass"] !== undefined) {
    actions(value["onPass"], report, `${path}.onPass`);
  }
}

/**
 * Why a value is not a standing change (ADR-146 decision 4.1): a whole number from -100 to 100, not 0. The object form
 * naming an organisation is reserved.
 *
 * @param value The `standing` value of an action or of `onFail`.
 * @returns The sentence for a refused value, null for a sound one.
 */
export function standingChangeProblem(value: unknown): string | null {
  if (isPlain(value)) {
    return "standing naming an organisation is reserved and refused until P2.22-T17 builds it";
  }
  if (!wholeIn(value, NPC_STANDING_MIN, NPC_STANDING_MAX) || value === 0) {
    return `standing must be a whole number from ${NPC_STANDING_MIN} to ${NPC_STANDING_MAX}, not 0`;
  }
  return null;
}

/**
 * Checks a check's `standingBonus`: a closed object naming the NPC and a whole `per` from 1 to 10. An organisation in
 * `with` is refused naming its row.
 *
 * @param value The `standingBonus` value.
 * @param report Records a problem.
 * @param path The path of the object.
 */
function standingBonusProblems(value: unknown, report: CheckReport, path: string): void {
  if (!isPlain(value)) {
    report(path, "standingBonus must be an object with with and per");
    return;
  }
  for (const key of Object.keys(value)) {
    if (!(STANDING_BONUS_FIELDS as readonly string[]).includes(key)) {
      report(`${path}.${key}`, `${key} is not a field of standingBonus`);
    }
  }
  if (value["with"] !== "npc") {
    report(`${path}.with`, "with must be npc; an organisation is refused until P2.22-T17 builds it");
  }
  if (!wholeIn(value["per"], MIN_STANDING_PER, MAX_STANDING_PER)) {
    report(`${path}.per`, `per must be a whole number from ${MIN_STANDING_PER} to ${MAX_STANDING_PER}`);
  }
}

/**
 * Checks a check's `modifiers`: a list of closed entries, each with conditions and a signed whole modifier from -100 to
 * 100 other than 0.
 *
 * @param value The `modifiers` value.
 * @param report Records a problem.
 * @param path The path of the list.
 * @param conditions Checks a `when` list.
 */
function modifiersProblems(value: unknown, report: CheckReport, path: string, conditions: (value: unknown, report: CheckReport, path: string) => void): void {
  if (!Array.isArray(value)) {
    report(path, "modifiers must be a list of entries with when and modifier");
    return;
  }
  value.forEach((entry, at) => {
    const here = `${path}[${at}]`;
    if (!isPlain(entry)) {
      report(here, "a modifier entry must be an object with when and modifier");
      return;
    }
    for (const key of Object.keys(entry)) {
      if (!(MODIFIER_FIELDS as readonly string[]).includes(key)) {
        report(`${here}.${key}`, `${key} is not a field of a modifier entry`);
      }
    }
    conditions(entry["when"], report, `${here}.when`);
    if (!wholeIn(entry["modifier"], -MAX_CHECK_MODIFIER, MAX_CHECK_MODIFIER) || entry["modifier"] === 0) {
      report(`${here}.modifier`, `modifier must be a whole number from -${MAX_CHECK_MODIFIER} to ${MAX_CHECK_MODIFIER}, not 0`);
    }
  });
}

/**
 * Checks a check's `onFail`: a closed object with an optional rule and standing change; `aggressive` is refused naming its row.
 *
 * @param value The `onFail` value.
 * @param report Records a problem.
 * @param path The path of the object.
 */
function failProblems(value: unknown, report: CheckReport, path: string): void {
  if (!isPlain(value)) {
    report(path, `onFail must be an object, with rule ${CHECK_FAIL_RULES.join(" or ")} or none for retry`);
    return;
  }
  for (const key of Object.keys(value)) {
    const row = RESERVED_FAIL_FIELDS[key];
    if (row !== undefined) {
      report(`${path}.${key}`, `${key} is reserved and refused until ${row} builds it`);
    } else if (key !== "rule" && key !== "standing") {
      report(`${path}.${key}`, `${key} is not a field of onFail`);
    }
  }
  if (value["rule"] !== undefined && !(CHECK_FAIL_RULES as readonly unknown[]).includes(value["rule"])) {
    report(`${path}.rule`, `rule must be ${CHECK_FAIL_RULES.join(" or ")}`);
  }
  if (value["standing"] !== undefined) {
    const problem = standingChangeProblem(value["standing"]);
    if (problem !== null) {
      report(`${path}.standing`, problem);
    }
  }
}

/**
 * The base a check starts from for one character: 30 plus every modifier whose conditions hold. The sum is not
 * clamped here; `checkChance` clamps the chance.
 *
 * @param modifiers The check's `modifiers`, if any.
 * @param holds Whether a list of conditions holds for the character.
 * @returns The base, in points of chance.
 * @example
 * checkBase([{ when: [], modifier: 20 }, { when: [], modifier: -15 }], () => true);
 * // 35
 */
export function checkBase(modifiers: readonly CheckModifier[] | undefined, holds: (when: readonly DialogueCondition[]) => boolean): number {
  let base = DEFAULT_CHECK_BASE;
  for (const entry of modifiers ?? []) {
    if (holds(entry.when)) {
      base += entry.modifier;
    }
  }
  return base;
}

/**
 * The chance a check passes, a whole percent: `clamp(base + 5 x (attribute - difficulty) + bonuses, 5, 95)`.
 *
 * @param base The base from `checkBase`.
 * @param attribute The character's current value of the checked attribute, with its effects and its background's bonus.
 * @param difficulty The check's difficulty.
 * @param bonuses Points of chance added outside the attribute: the check's `standingBonusPoints`, and later membership and notoriety.
 * @returns The chance, from 5 to 95.
 * @example
 * checkChance(30, 12, 10, 1);
 * // 41
 */
export function checkChance(base: number, attribute: number, difficulty: number, bonuses: number): number {
  const raw = base + CHECK_POINTS_PER_ATTRIBUTE * (attribute - difficulty) + bonuses;
  return Math.min(MAX_CHECK_CHANCE, Math.max(MIN_CHECK_CHANCE, Math.trunc(raw)));
}

/**
 * Whether a roll passes a check (ADR-136's amendment of 2026-10-28, decision 8): the digest's remainder modulo 100 below
 * the chance.
 *
 * @param digest The roll's unsigned 32-bit digest.
 * @param chance The chance from `checkChance`, a whole percent.
 * @returns True when the roll passes.
 * @example
 * checkPasses(104, 5);
 * // true
 */
export function checkPasses(digest: number, chance: number): boolean {
  return digest % 100 < chance;
}
